'use client';

import { useCallback, useEffect, useMemo, useState, type ComponentPropsWithRef, type ReactElement } from 'react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { cn } from '@/lib/utils';
import {
    PromoBannerRotatorContext,
    type PromoBannerSlotRegistration,
    type PromoBannerRotatorContextValue,
} from './promo-banner-rotator-context';

type PromoBannerRotatorProps = ComponentPropsWithRef<'div'>;

/**
 * Cycles the assigned promo banners. Banners stack in one grid cell so swapping never shifts
 * the page. Rotation is off in design mode so every banner stays selectable.
 */
export const PromoBannerRotator = ({ children, className, ...props }: PromoBannerRotatorProps): ReactElement => {
    const { isDesignMode } = usePageDesignerMode();
    const [slots, setSlots] = useState<PromoBannerSlotRegistration[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isHydrated, setIsHydrated] = useState(false);

    const register = useCallback((registration: PromoBannerSlotRegistration) => {
        setSlots((previous) =>
            previous.some((slot) => slot.id === registration.id) ? previous : [...previous, registration]
        );
        return () => setSlots((previous) => previous.filter((slot) => slot.id !== registration.id));
    }, []);

    // Child effects flush first, so every banner has registered by now.
    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const count = slots.length;
    const activeSlot = count > 0 ? slots[activeIndex % count] : undefined;

    useEffect(() => {
        if (isDesignMode || isPaused || count < 2 || !activeSlot) {
            return;
        }

        const timer = window.setTimeout(() => {
            setActiveIndex((previous) => (previous + 1) % count);
        }, activeSlot.intervalMs);

        return () => window.clearTimeout(timer);
    }, [activeSlot, count, isDesignMode, isPaused]);

    const value = useMemo<PromoBannerRotatorContextValue>(
        () => ({
            register,
            activeId: isDesignMode || !isHydrated ? null : (activeSlot?.id ?? null),
        }),
        [activeSlot, isDesignMode, isHydrated, register]
    );

    return (
        <PromoBannerRotatorContext.Provider value={value}>
            <div
                data-slot="promo-banner-rotator"
                data-hydrated={isHydrated}
                className={cn(
                    'grid w-full [&>*]:col-start-1 [&>*]:row-start-1',
                    // `hidden` so non-active banners are removed from the accessibility tree pre-hydration.
                    'data-[hydrated=false]:[&>*:not(:first-child)]:hidden',
                    className
                )}
                onPointerEnter={() => setIsPaused(true)}
                onPointerLeave={() => setIsPaused(false)}
                onFocusCapture={() => setIsPaused(true)}
                onBlurCapture={() => setIsPaused(false)}
                {...props}>
                {children}
            </div>
        </PromoBannerRotatorContext.Provider>
    );
};
